import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import logoSvg from "../../../branding/logo.svg";
import logoWhiteSvg from "../../../branding/logo_white.svg";
import { SearchModal } from "./SearchModal";
import { ThemeToggle } from "./ThemeToggle";
import { AnnouncementBar } from "./AnnouncementBar";
import { NavOffcanvas, navOffcanvasMap } from "./NavOffcanvas";
import { getPageStatuses, getFeatureFlags } from "../utils/adminStore";

const navLinks = [
  { path: "/", label: "Home" },
  { path: "/services", label: "Services" },
  { path: "/courses", label: "Courses" },
  { path: "/blog", label: "Blog" },
  { path: "/about", label: "About" },
  { path: "/pricing", label: "Pricing" },
  { path: "/careers", label: "Careers" },
  { path: "/contact", label: "Contact" },
];

export function Header({ currentPath }: { currentPath: string }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [hoveredNavPath, setHoveredNavPath] = useState<string | null>(null);

  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [pageStatuses, setPageStatuses] = useState(getPageStatuses());
  const [flags, setFlags] = useState(getFeatureFlags());

  useEffect(() => {
    const sync = () => {
      setPageStatuses(getPageStatuses());
      setFlags(getFeatureFlags());
    };
    window.addEventListener("radiece_admin_update", sync);
    return () => window.removeEventListener("radiece_admin_update", sync);
  }, []);

  const visibleNavLinks = navLinks.filter((link) => {
    if (link.path === "/careers" && !flags.enableCareers) return false;
    if (link.path === "/pricing" && !flags.enablePricingCalculator) return false;
    if (link.path === "/blog" && !flags.enableBlog) return false;
    const status = pageStatuses.find((p) => p.path === link.path);
    return !status || status.status === "published";
  });

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 10);
      setHoveredNavPath(null);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    setMobileOpen(false);
    setHoveredNavPath(null);
  }, [currentPath]);

  // Keyboard navigation: Escape key closes offcanvas
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setHoveredNavPath(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleNavMouseEnter = (path: string) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (navOffcanvasMap[path]) {
      setHoveredNavPath(path);
    } else {
      setHoveredNavPath(null);
    }
  };

  const handleNavMouseLeave = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setHoveredNavPath(null);
    }, 240);
  };

  const handleOffcanvasMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const handleOffcanvasMouseLeave = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setHoveredNavPath(null);
    }, 180);
  };

  const handleLinkClick = () => {
    setHoveredNavPath(null);
    setMobileOpen(false);
  };

  return (
    <>
      <header
        className={`no-print fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/98 dark:bg-[#090e17]/95 shadow-md backdrop-blur-md border-b border-slate-200/40 dark:border-slate-800/80"
            : "bg-white/95 dark:bg-[#090e17]/90 backdrop-blur-sm shadow-xs border-b border-transparent dark:border-slate-800/50"
        }`}
      >
        {/* Top Announcement Bar */}
        <AnnouncementBar />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-3 shrink-0" aria-label="RADIECE Homepage" onClick={handleLinkClick}>
            <motion.img
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              src={logoSvg}
              alt="RADIECE Logo"
              width={160}
              height={40}
              className="h-8.5 sm:h-9.5 w-auto object-contain dark:hidden"
            />
            <motion.img
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              src={logoWhiteSvg}
              alt="RADIECE Logo"
              width={160}
              height={40}
              className="h-8.5 sm:h-9.5 w-auto object-contain hidden dark:block"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4 lg:gap-6">
            <nav className="flex items-center gap-4 lg:gap-6" aria-label="Main navigation">
              {visibleNavLinks.map((link) => {
                const isActive = currentPath === link.path;
                const isHovered = hoveredNavPath === link.path;
                const hasOffcanvas = Boolean(navOffcanvasMap[link.path]);

                return (
                  <div
                    key={link.path}
                    className="relative py-2 px-1"
                    onMouseEnter={() => handleNavMouseEnter(link.path)}
                    onMouseLeave={handleNavMouseLeave}
                  >
                    {isHovered && (
                      <motion.div
                        layoutId="navHoverPill"
                        className="absolute inset-0 rounded-lg bg-slate-100/80 dark:bg-slate-800/60 -z-10"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ type: "spring", stiffness: 350, damping: 28 }}
                      />
                    )}

                    <a
                      href={link.path}
                      onClick={handleLinkClick}
                      aria-current={isActive ? "page" : undefined}
                      className={`relative text-sm font-medium transition-colors py-1.5 px-2 inline-flex items-center gap-1 cursor-pointer ${
                        isActive || isHovered
                          ? "text-brand dark:text-primary font-bold"
                          : "text-slate-600 dark:text-slate-300 hover:text-brand dark:hover:text-primary"
                      }`}
                    >
                      <span>{link.label}</span>
                      {hasOffcanvas && (
                        <motion.svg
                          animate={{ rotate: isHovered ? 180 : 0 }}
                          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                          className={`w-3 h-3 ${
                            isHovered ? "text-brand dark:text-primary" : "text-slate-400 dark:text-slate-500 opacity-60"
                          }`}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </motion.svg>
                      )}

                      {isActive && (
                        <motion.span
                          layoutId="activeNavIndicator"
                          className="absolute bottom-0 left-2 right-2 h-0.5 bg-brand dark:bg-primary rounded-full shadow-[0_0_8px_rgba(57,183,233,0.6)]"
                          transition={{ type: "spring", stiffness: 380, damping: 30 }}
                        />
                      )}
                    </a>
                  </div>
                );
              })}
            </nav>

            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              id="search-trigger"
              onClick={() => {
                setHoveredNavPath(null);
                setSearchOpen(true);
              }}
              aria-label="Search website"
              className="inline-flex items-center gap-2 bg-slate-100 dark:bg-slate-800/80 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 hover:text-brand dark:hover:text-white px-3.5 py-1.5 rounded-full text-xs font-medium transition-all border border-slate-200 dark:border-slate-700 cursor-pointer"
            >
              <svg className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <span>Search...</span>
              <kbd className="hidden lg:inline bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 rounded text-xs text-slate-700 dark:text-slate-300 font-mono border border-slate-300 dark:border-slate-600">
                ⌘K
              </kbd>
            </motion.button>

            {/* Theme Mode Switcher */}
            <ThemeToggle variant="segmented" />
          </div>

          {/* Mobile Right Controls */}
          <div className="flex items-center gap-1.5 sm:gap-2 md:hidden">
            <ThemeToggle variant="cycle" />

            <button
              onClick={() => setSearchOpen(true)}
              aria-label="Search website"
              className="p-2 min-w-[40px] min-h-[40px] text-slate-600 dark:text-slate-300 hover:text-brand dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors flex items-center justify-center cursor-pointer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
            </button>

            <button
              className="text-2xl text-brand/80 dark:text-slate-200 hover:text-brand dark:hover:text-white transition-colors p-2 min-w-[40px] min-h-[40px] flex items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle navigation menu"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? "✕" : "☰"}
            </button>
          </div>
        </div>

        {/* Desktop Horizontal Offcanvas Mega-Menu (Visible on Hover) */}
        <AnimatePresence>
          {hoveredNavPath && (
            <div
              onMouseEnter={handleOffcanvasMouseEnter}
              onMouseLeave={handleOffcanvasMouseLeave}
            >
              <NavOffcanvas
                activePath={hoveredNavPath}
                onClose={() => setHoveredNavPath(null)}
                onItemSelect={handleLinkClick}
              />
            </div>
          )}
        </AnimatePresence>

        {/* Mobile Dropdown Navigation */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              className="md:hidden bg-white dark:bg-[#090e17] border-t border-slate-100 dark:border-slate-800 shadow-xl overflow-hidden"
              aria-label="Mobile navigation"
            >
              <div className="p-3 border-b border-slate-100 dark:border-slate-800 space-y-3">
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    setSearchOpen(true);
                  }}
                  className="w-full flex items-center gap-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 px-4 py-2.5 rounded-lg text-xs font-medium transition-colors"
                >
                  <svg className="w-4 h-4 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <span>Search services, courses, blog & news...</span>
                </button>

                <div className="flex items-center justify-between px-1">
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Theme</span>
                  <ThemeToggle variant="segmented" showLabel />
                </div>
              </div>

              {visibleNavLinks.map((link) => (
                <a
                  key={link.path}
                  href={link.path}
                  onClick={() => setMobileOpen(false)}
                  aria-current={currentPath === link.path ? "page" : undefined}
                  className={`block px-6 py-3.5 text-sm font-medium transition-colors border-b border-slate-50 dark:border-slate-800/60 min-h-[44px] flex items-center ${
                    currentPath === link.path
                      ? "bg-slate-100 dark:bg-slate-800/80 text-brand-dark dark:text-primary font-semibold"
                      : "text-brand/80 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/50 hover:text-brand-dark dark:hover:text-white"
                  }`}
                >
                  {link.label}
                </a>
              ))}
              <a
                href="/news"
                onClick={() => setMobileOpen(false)}
                className="block px-6 py-3.5 text-sm font-medium text-primary hover:bg-slate-100 dark:hover:bg-slate-800/50 min-h-[44px] flex items-center gap-2"
              >
                <span>📰</span> Newsfeed & Dispatches
              </a>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
